import { Truck } from "lucide-react"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

interface TrackingNumberLineProps {
  /** The brand-populated tracking string (≤80 chars, no carrier link in v1). */
  value: string
  /**
   * data-testid for the button. Defaults to the original CollabCard testid so
   * existing specs stay green; the §05 MissionCard passes
   * `mission-card-tracking`.
   */
  testId?: string
  className?: string
  /**
   * OKS-563 — i18n key for the copy-success toast. Defaults to the mission
   * gifting string so every existing call site stays byte-identical; the
   * rewards wallet passes `rewards.mine.tracking.copied`.
   *
   * The AFFORDANCE is shared (PRD §3 — two shipping surfaces, one vocabulary,
   * zero shared machinery), the COPY is not: a creator reading a reward row
   * should not be told about a collab. Two string props rather than a
   * `surface: "collab" | "reward"` enum, because an enum would put the rewards
   * copy inside a collab component — the coupling that ruling exists to avoid.
   */
  copiedMessageKey?: string
  /** OKS-563 — i18n key for the aria-label. Interpolates `value`. */
  ariaLabelKey?: string
  /**
   * OKS-563 — optional i18n key wrapping the reference in a label,
   * interpolating `number`. Omitted → the bare value renders, which is
   * what every collab call site does and must keep doing.
   *
   * The rewards wallet passes `rewards.mine.tracking.present`
   * ("Tracking #%{number}") because UX Spec §3 makes that string binding
   * and the brand queue already renders the same framing. Without it the
   * two surfaces show the same reference two different ways.
   */
  labelKey?: string
}

/**
 * OKS-32 — Tracking-number row for Drop `shipped` / `delivered` cards.
 * Extracted to a shared component in OKS-480 so the status-driven `MissionCard`
 * reuses the exact copy-on-tap affordance instead of rebuilding it (TRD KD-6).
 *
 * Renders a Truck icon + monospace tracking string. On mobile, tap-to-copy via
 * the Clipboard API + Sonner toast (UX Spec §4). On desktop, the standard
 * browser long-press / right-click "copy" gesture works — we don't override.
 *
 * Truncation:
 *   - Mobile: truncates + tap-to-expand inline.
 *   - Desktop: full string visible (`md:overflow-visible`).
 *
 * Copy behaviour is best-effort — a no-op when the Clipboard API isn't
 * available (older Safari on insecure origins); the native long-press still
 * copies.
 */
export function TrackingNumberLine({
  value,
  testId = "collab-card-tracking-number",
  className,
  copiedMessageKey = "collabs.tracking_number.copied",
  ariaLabelKey = "collabs.tracking_number.aria",
  labelKey,
}: TrackingNumberLineProps) {
  const { t } = useTranslation()

  function handleClick() {
    if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
      navigator.clipboard
        .writeText(value)
        .then(() => {
          toast.success(t(copiedMessageKey))
        })
        .catch(() => {
          // Best-effort copy; swallow errors silently. Long-press still works
          // via the browser's native gesture.
        })
    }
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      className={
        className ??
        "text-muted-foreground hover:bg-muted/50 -mx-1 flex items-center gap-1.5 rounded-md px-1 py-0.5 text-left text-xs transition-colors"
      }
      data-testid={testId}
      aria-label={t(ariaLabelKey, { value })}
    >
      <Truck className="size-3.5 shrink-0" aria-hidden="true" />
      <span className="truncate font-mono md:overflow-visible md:whitespace-normal">
        {labelKey ? t(labelKey, { number: value }) : value}
      </span>
    </button>
  )
}
